import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/**
 * Determinate circular progress indicator.
 *
 * There is no determinate progress element anywhere else in the app (Material's
 * `mat-progress-spinner` is only used indeterminately), so this is a plain inline
 * SVG: a track plus a second circle whose visible arc is driven by
 * `stroke-dasharray` / `stroke-dashoffset`. At intermediate progress, the track
 * starts after the active arc and leaves a visible gap at both ends. All
 * geometry is derived in computed signals — the template only reads values.
 *
 * The arc starts at 12 o'clock (the whole `<svg>` is rotated -90deg; the track is
 * a symmetric circle so the rotation is invisible on it) and uses round line caps.
 *
 * Colors: the two strokes read `--dd-progress-ring-track` / `--dd-progress-ring-value`
 * with a theme-aware fallback defined on the host. Because the consumable names are
 * NOT set on `:host`, any ancestor can recolor the ring by declaring them — which is
 * what the getting-started panel does to apply its accent ring on the dark surface.
 *
 * ```html
 * <app-progress-ring [value]="2" [max]="3" ariaLabel="Voortgang: 2 van 3 afgerond" />
 * ```
 */
@Component({
  selector: 'app-progress-ring',
  template: `
    <svg
      class="dd-progress-ring"
      [attr.width]="size()"
      [attr.height]="size()"
      [attr.viewBox]="viewBox()"
      role="progressbar"
      [attr.aria-label]="ariaLabel()"
      [attr.aria-valuenow]="clampedValue()"
      aria-valuemin="0"
      [attr.aria-valuemax]="safeMax()"
      data-cy="progress-ring">
      <circle
        class="dd-progress-ring__track"
        fill="none"
        stroke-linecap="round"
        [attr.cx]="center()"
        [attr.cy]="center()"
        [attr.r]="radius()"
        [attr.stroke-width]="safeStrokeWidth()"
        [attr.stroke-dasharray]="trackDashArray()"
        [attr.stroke-dashoffset]="trackDashOffset()" />
      <!-- Skipped at 0 so the round line cap does not render a stray dot. -->
      @if (fraction() > 0) {
        <circle
          class="dd-progress-ring__value"
          fill="none"
          stroke-linecap="round"
          [attr.cx]="center()"
          [attr.cy]="center()"
          [attr.r]="radius()"
          [attr.stroke-width]="safeStrokeWidth()"
          [attr.stroke-dasharray]="valueDashArray()" />
      }
      @if (isComplete()) {
        <path
          class="dd-progress-ring__check"
          d="M12 18.5 16 22.5 24 13.5"
          fill="none"
          [attr.transform]="checkTransform()" />
      }
    </svg>
  `,
  styles: `
    /* Host-level rules MUST use :host — a bare class selector is scoped to
       [_ngcontent] and never matches the host element. */
    :host {
      display: inline-flex;
      flex: none;
      line-height: 0;

      /* Theme-aware fallbacks. Light default here, dark override below. */
      --dd-progress-ring-track-fallback: #eeeff1; /* Figma border/default */
      --dd-progress-ring-value-fallback: #0f172a; /* slate-900 */
    }

    :host-context(.dark) {
      --dd-progress-ring-track-fallback: rgba(255, 255, 255, 0.14);
      --dd-progress-ring-value-fallback: #f8fafc; /* slate-50 */
    }

    .dd-progress-ring {
      display: block;
    }

    .dd-progress-ring__track,
    .dd-progress-ring__value {
      /* Start the arc at 12 o'clock instead of 3 o'clock. */
      transform: rotate(-90deg);
      transform-origin: center;
    }

    .dd-progress-ring__track {
      stroke: var(--dd-progress-ring-track, var(--dd-progress-ring-track-fallback));
    }

    .dd-progress-ring__value {
      stroke: var(--dd-progress-ring-value, var(--dd-progress-ring-value-fallback));
    }

    .dd-progress-ring__track,
    .dd-progress-ring__value {
      transition:
        stroke-dasharray 0.35s ease,
        stroke-dashoffset 0.35s ease;
    }

    .dd-progress-ring__check {
      stroke: var(--dd-progress-ring-value, var(--dd-progress-ring-value-fallback));
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-width: 3;
    }

    @media (prefers-reduced-motion: reduce) {
      .dd-progress-ring__track,
      .dd-progress-ring__value {
        transition: none;
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProgressRingComponent {
  /** Completed amount. Clamped into [0, max]. */
  readonly value = input.required<number>();

  /** Total amount. `<= 0` renders an empty track. */
  readonly max = input.required<number>();

  /** Outer diameter in px. */
  readonly size = input(36);

  /** Stroke thickness in px, for both track and arc. Figma "Progress bar/ circular" = 4dp. */
  readonly strokeWidth = input(4);

  /** Visible space in px between active arc and remaining track. */
  readonly gapWidth = input(3);

  /** Accessible name for the progressbar role. */
  readonly ariaLabel = input('Voortgang');

  /** Stroke can never be thicker than the radius, otherwise the arc collapses. */
  readonly safeStrokeWidth = computed(() => {
    const upperBound = Math.max(1, this.size() / 2 - 1);
    return Math.min(Math.max(this.strokeWidth(), 1), upperBound);
  });

  readonly center = computed(() => this.size() / 2);

  readonly radius = computed(() => Math.max(1, (this.size() - this.safeStrokeWidth()) / 2));

  readonly viewBox = computed(() => `0 0 ${this.size()} ${this.size()}`);

  /** Scale the 36px Figma check geometry with the configurable ring size. */
  readonly checkTransform = computed(() => `scale(${this.size() / 36})`);

  readonly safeMax = computed(() => Math.max(0, this.max()));

  readonly clampedValue = computed(() => Math.min(Math.max(this.value(), 0), this.safeMax()));

  readonly fraction = computed(() => {
    const max = this.safeMax();
    return max > 0 ? this.clampedValue() / max : 0;
  });

  readonly isComplete = computed(() => this.safeMax() > 0 && this.clampedValue() === this.safeMax());

  readonly circumference = computed(() => 2 * Math.PI * this.radius());

  readonly activeArcLength = computed(() => this.circumference() * this.fraction());

  readonly hasTrackGaps = computed(() => this.fraction() > 0 && this.fraction() < 1);

  /**
   * Round caps extend half a stroke beyond both arc endpoints. Add one stroke
   * width to preserve the requested visible gap between those caps.
   */
  readonly gapLength = computed(() => {
    if (!this.hasTrackGaps()) {
      return 0;
    }

    const requestedGap = Math.max(0, this.gapWidth()) + this.safeStrokeWidth();
    const remainingLength = this.circumference() - this.activeArcLength();
    return Math.min(requestedGap, remainingLength / 2);
  });

  readonly trackArcLength = computed(() => {
    if (!this.hasTrackGaps()) {
      return this.circumference();
    }

    return Math.max(0, this.circumference() - this.activeArcLength() - 2 * this.gapLength());
  });

  readonly valueDashArray = computed(() => {
    const activeLength = this.activeArcLength();
    return `${activeLength} ${this.circumference() - activeLength}`;
  });

  readonly trackDashArray = computed(() => {
    const trackLength = this.trackArcLength();
    return `${trackLength} ${this.circumference() - trackLength}`;
  });

  readonly trackDashOffset = computed(() => (this.hasTrackGaps() ? -(this.activeArcLength() + this.gapLength()) : 0));
}
